/**
 * Phase 3 — the guillotine (SPEC §2.3), which is not a screen, and is not
 * guaranteed to happen.
 *
 * **It may never run.** If every connected predictor has locked in before the
 * ranking lands, the turn goes straight from `rank-and-predict` to `reveal` and
 * this route is never mounted — no strip, no chime, no ten seconds of animation
 * for a room that has already finished (SPEC §2.3 phase 3, as amended). Nothing
 * is staged on this phase arriving: everything the countdown shows is a state of
 * the screen underneath it, which the previous phase already had on screen.
 *
 * When it does run, the point (DESIGN.md §3.5) is that nothing *arrives*: the
 * prediction screen the player was already on gains a fixed strip at the top and
 * is otherwise untouched, so the ten seconds are ten seconds of the same screen
 * rather than an interruption to it. That is why this route renders the same
 * `PredictionFlow` as phase 2 and passes the strip in.
 *
 * The picker stays live for those ten seconds — predictions hard-lock *at zero*,
 * not when the turn player submits — and goes inert on this device's own reading
 * of the server clock, not on the arrival of `predictions_locked`. Anyone
 * without both picks at zero scores nothing, no partial credit.
 *
 * The chime is fired by the store when `lock_countdown` arrives (SPEC §5.6), so
 * it lands on every phone at once regardless of which route is mounted — and on
 * the skip path no frame arrives, so no chime fires and the reveal sting is the
 * turn's only cue.
 */
import { Countdown, Screen, useCountdown } from '../../components/index.js';
import { useIsTurnPlayer, useLock } from '../../store/index.js';
import { PredictionFlow } from './PredictionPad.js';

export function LockCountdownScreen() {
  const isTurnPlayer = useIsTurnPlayer();
  const lock = useLock();
  // `localEndsAt` is already on this device's clock, so the offset is zero here.
  const countdown = useCountdown({ endsAt: lock?.localEndsAt ?? 0 });
  // No lock frame yet means the phase has arrived a beat before its clock, not
  // that the clock has run out: an absent strip is right, a black one is a lie.
  const strip = lock ? <Countdown {...countdown} /> : undefined;
  const expired = lock !== null && countdown.expired;

  /*
    The turn player's screen is quiet, and that is the whole specification for
    it. It carried a live prediction count and a line about what everyone else
    could still do; both are gone. SPEC §2.3 phase 2 says they are told nothing
    about the predictions — "not who, not what, not how many" — and a sentence
    describing other people's editing window is the same information with the
    number filed off. They have submitted; the strip says how long is left.
  */
  if (isTurnPlayer) {
    return (
      <Screen
        testId="lock-screen"
        strip={strip}
        eyebrow="Your turn"
        meta={expired ? 'Locked' : 'Locking'}
        title="Ranking submitted."
      >
        <div className="flex-1" />
      </Screen>
    );
  }

  return <PredictionFlow strip={strip} hardLocked={expired} />;
}
